import {
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CDatePicker,
  CForm,
  CFormFeedback,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CTimePicker,
} from '@coreui/react-pro'
import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'

import FixtureService from 'src/service/FixtureService'
import TeamService from 'src/service/TeamService'
import ToastComponent from 'src/components/common/TaostComponent'

const EditForm = ({
  fixtureId,
  visible,
  onClose,
  gameType,
  activePage,
  columnFilter,
  columnSorter,
  itemsPerPage,
  fetchData,
}) => {
  const [fixtureDetail, setFixtureDetail] = useState(null)
  const [teamList, setTeamList] = useState([])
  const [matchTypeList, setMatchTypeList] = useState([])

  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')

  const [loader, setLoader] = useState(false)

  useEffect(() => {
    if (visible && fixtureId) {
      FixtureService.getFixtureDetailOld(fixtureId).then((res) => {
        if (res.status === 200) {
          const data = res.data
          setFixtureDetail(data)

          setStartDate(data.start_date ? new Date(data.start_date) : null)
          setEndDate(data.end_date ? new Date(data.end_date) : null)
          setStartTime(data.start_time || '')
          setEndTime(data.end_time || '')

          TeamService.getTeamListByGrade(data.grade).then((teamRes) => {
            if (teamRes.status === 200) {
              setTeamList(teamRes.data || [])
              setMatchTypeList(teamRes.matchTypeList || [])
            }
          })
        }
      })
    }
  }, [visible, fixtureId])

  const validationSchema =
    gameType === 1
      ? Yup.object({ team: Yup.string().required('Team is required') })
      : Yup.object({
          team: Yup.string().required('Home team is required'),
          team2: Yup.string().required('Away team is required'),
        })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      team: fixtureDetail?.team || '',
      team2: fixtureDetail?.team2 || '',
      match_type: fixtureDetail?.match_code || '',
      vanue: fixtureDetail?.vanue || '',
      opposition_club: fixtureDetail?.opposition_club || '',
      start_date: fixtureDetail?.start_date || '',
      end_date: fixtureDetail?.end_date || '',
      start_time: fixtureDetail?.start_time || '',
      end_time: fixtureDetail?.end_time || '',
    },
    validationSchema,
    onSubmit: (values) => {
      setLoader(true)
      const payload = {
        ...values,
        fixtureId: fixtureDetail.id,
        start_date: startDate ? moment(startDate).format('YYYY-MM-DD') : '',
        end_date: endDate ? moment(endDate).format('YYYY-MM-DD') : '',
        start_time: startTime,
        end_time: endTime,
      }

      FixtureService.editFixture(payload)
        .then((res) => {
          if (res.status === 200) {
            ToastComponent(res.message, 'success')
            fetchData(activePage, columnFilter, columnSorter, itemsPerPage)
            onClose()
          }
        })
        .catch((e) => {
          ToastComponent(e.response?.data?.message || 'Something went wrong', 'error')
        })
        .finally(() => setLoader(false))
    },
  })

  return (
    <COffcanvas placement="end" visible={visible} onHide={onClose} backdrop style={{ width: 640 }}>
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Edit Fixture</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Update fixture teams, timing and match type.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={onClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        <CForm className="row g-3 flex-grow-1" onSubmit={formik.handleSubmit}>
          {gameType === 1 ? (
            <CCol xs={6}>
              <CFormLabel className="fw-bold">Team *</CFormLabel>
              <CFormSelect
                name="team"
                value={formik.values.team}
                onChange={formik.handleChange}
                className={formik.touched.team && formik.errors.team ? 'is-invalid' : ''}
              >
                <option value="">Select Team</option>
                {teamList.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </CFormSelect>
              <CFormFeedback invalid>{formik.errors.team}</CFormFeedback>
            </CCol>
          ) : (
            <>
              <CCol xs={6}>
                <CFormLabel className="fw-bold">Home Team *</CFormLabel>
                <CFormSelect
                  name="team"
                  value={formik.values.team}
                  onChange={formik.handleChange}
                  className={formik.touched.team && formik.errors.team ? 'is-invalid' : ''}
                >
                  <option value="">Select Home Team</option>
                  {teamList.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </CFormSelect>
                <CFormFeedback invalid>{formik.errors.team}</CFormFeedback>
              </CCol>
              <CCol xs={6}>
                <CFormLabel className="fw-bold">Away Team *</CFormLabel>
                <CFormSelect
                  name="team2"
                  value={formik.values.team2}
                  onChange={formik.handleChange}
                  className={formik.touched.team2 && formik.errors.team2 ? 'is-invalid' : ''}
                >
                  <option value="">Select Away Team</option>
                  {teamList.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </CFormSelect>
                <CFormFeedback invalid>{formik.errors.team2}</CFormFeedback>
              </CCol>
            </>
          )}

          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="match_type">
              Match Type *
            </CFormLabel>
            <CFormSelect
              aria-label="Select Match Type"
              name="match_type"
              id="match_type"
              value={formik.values.match_type}
              disabled
            >
              <option value={0}>Select Match Type</option>
              {matchTypeList &&
                matchTypeList.map((item, key) => (
                  <option value={item?.id} key={key}>
                    {item?.name}
                  </option>
                ))}
            </CFormSelect>
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold">Start Date *</CFormLabel>
            <CDatePicker
              date={startDate}
              format="dd/MM/yyyy"
              placeholder="Start Date"
              cleaner={false}
              onDateChange={(date) => {
                setStartDate(date)
                setEndDate(null)
                formik.setFieldTouched('start_date', true)
              }}
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold">End Date *</CFormLabel>
            <CDatePicker
              date={endDate}
              minDate={startDate}
              format="dd/MM/yyyy"
              placeholder="End Date"
              cleaner={false}
              onDateChange={(date) => {
                setEndDate(date)
                formik.setFieldTouched('end_date', true)
              }}
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold">Start Time *</CFormLabel>
            <CTimePicker
              time={startTime}
              ampm
              seconds={false}
              cleaner={false}
              locale="en-US"
              onTimeChange={(t) => {
                const time24 = moment(t, ['hh:mm A', 'HH:mm']).format('HH:mm')
                setStartTime(time24)
                formik.setFieldTouched('start_time', true)
              }}
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold">End Time *</CFormLabel>
            <CTimePicker
              time={endTime}
              ampm
              seconds={false}
              cleaner={false}
              locale="en-US"
              onTimeChange={(t) => {
                const time24 = moment(t, ['hh:mm A', 'HH:mm']).format('HH:mm')
                setEndTime(time24)
                formik.setFieldTouched('end_time', true)
              }}
            />
          </CCol>
        </CForm>
        {fixtureDetail?.status === 2 && (
          <div className="text-medium-emphasis small mt-3">
            This fixture is completed and can no longer be edited.
          </div>
        )}
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={onClose}>
          Cancel
        </CButton>
        {fixtureDetail?.status !== 2 && (
          <CLoadingButton color="primary" loading={loader} onClick={() => formik.handleSubmit()}>
            Save Changes
          </CLoadingButton>
        )}
      </div>
    </COffcanvas>
  )
}

export default EditForm
